/**
 * LeaderboardPage.jsx
 * 명예의 전당 (XP 랭킹) 페이지 - Premium Command Center Style
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../services/api';
import Header from '../components/layout/Header';
import TermsModal from '../components/modals/TermsModal';
import useLunaStore from '../store/useLunaStore';
import { getUserFullTitle } from '../utils/userTitles';

export default function LeaderboardPage() {
    const navigate = useNavigate();
    const { username, setIsSoundOn, isSoundOn } = useLunaStore();
    const [leaders, setLeaders] = useState([]);
    const [myRank, setMyRank] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showTerms, setShowTerms] = useState(false);

    // 랭킹 데이터 로드
    const loadLeaderboard = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const data = await api.getLeaderboard();
            setLeaders(data.leaders || []);
            setMyRank(data.my_rank || null);
        } catch (err) {
            console.error('랭킹 로드 실패:', err);
            setError(err.message || '랭킹을 불러올 수 없습니다.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLeaderboard();
    }, [loadLeaderboard]);

    // Top 3 포디움
    const top3 = leaders.slice(0, 3);
    const restOfRanking = leaders.slice(3);

    return (
        <div className="h-screen overflow-y-auto bg-[#0B0E14] text-slate-200 font-sans selection:bg-cyan-500/30 overflow-x-hidden scrollbar-thin scrollbar-track-[#0B0E14] scrollbar-thumb-white/10 hover:scrollbar-thumb-cyan-500/50">
            {/* Background - Grid + Aurora Blobs */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
                <div className="aurora-blob blob-1"></div>
                <div className="aurora-blob blob-2"></div>
                <div className="aurora-blob blob-3"></div>
                <div className="bg-grid-pattern"></div>
            </div>

            <Header
                toggleSound={() => setIsSoundOn(!isSoundOn)}
                isSoundOn={isSoundOn}
                username={username}
                onLogout={() => { /* Logout logic handles internally in Header or separate store action */ }}
                onShowTerms={() => setShowTerms(true)}
            />

            <TermsModal isOpen={showTerms} onClose={() => setShowTerms(false)} />

            <main className="max-w-[1400px] mx-auto px-6 py-6 pb-32">

                {/* Header Section - Compact */}
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                            <span className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
                                <i className="fas fa-trophy text-lg"></i>
                            </span>
                            Hall of Fame
                        </h1>
                        <p className="text-slate-400 text-sm mt-2 ml-1">
                            Top operatives ranked by XP accumulation.
                        </p>
                    </div>
                </div>

                {/* 로딩 상태 */}
                {isLoading && (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-16 h-16 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mb-4 shadow-[0_0_20px_rgba(34,211,238,0.2)]"></div>
                        <p className="text-cyan-500/80 font-mono text-sm tracking-widest animate-pulse">SYNCING DATABASE...</p>
                    </div>
                )}

                {/* 에러 상태 */}
                {error && !isLoading && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center backdrop-blur-md max-w-lg mx-auto">
                        <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
                        <h3 className="text-lg font-bold text-white mb-2">Data Retrieval Failed</h3>
                        <p className="text-red-200/60 mb-6 text-sm">{error}</p>
                        <button
                            onClick={loadLeaderboard}
                            className="px-6 py-2 bg-red-600 hover:bg-red-500 text-white rounded-xl transition-all font-bold text-sm shadow-lg shadow-red-900/20"
                        >
                            <i className="fas fa-redo mr-2"></i>Retry Connection
                        </button>
                    </div>
                )}

                {/* 랭킹 컨텐츠 */}
                {!isLoading && !error && (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

                        {/* LEFT COLUMN: Top 3 Podium (Visual) */}
                        <div className="lg:col-span-4 space-y-4">
                            {/* Rank 1 Card */}
                            {top3[0] && (
                                <div className="relative overflow-hidden rounded-2xl glass-panel p-6 group border-amber-500/30">
                                    <div className="absolute inset-0 bg-amber-500/5 group-hover:bg-amber-500/10 transition-colors duration-500"></div>
                                    <div className="absolute top-0 right-0 p-4 opacity-10">
                                        <i className="fas fa-crown text-9xl text-amber-500 -rotate-12 transform translate-x-4 -translate-y-4"></i>
                                    </div>

                                    <div className="relative z-10 flex flex-col items-center text-center">
                                        <div className="relative">
                                            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#1a1e2e] to-[#0f1219] p-1 border-2 border-amber-500 shadow-[0_0_30px_rgba(245,158,11,0.3)] mb-4 flex items-center justify-center">
                                                <span className="text-4xl font-bold text-amber-400">{top3[0].username?.[0]?.toUpperCase() || '1'}</span>
                                            </div>
                                            <div className="absolute -top-3 -right-3 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold border-4 border-[#0B0E14] shadow-lg">1</div>
                                        </div>

                                        <h2 className="text-2xl font-bold text-white mb-1">{top3[0].nickname || top3[0].username}</h2>
                                        <div className="text-xs text-amber-400 font-mono mb-4 px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 inline-block">
                                            {getUserFullTitle(top3[0].level, [])}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 w-full">
                                            <div className="bg-black/20 rounded-xl p-2 text-center">
                                                <div className="text-[10px] text-slate-500 uppercase font-mono">Total XP</div>
                                                <div className="text-lg font-bold text-amber-400 font-mono">{top3[0].xp?.toLocaleString()}</div>
                                            </div>
                                            <div className="bg-black/20 rounded-xl p-2 text-center">
                                                <div className="text-[10px] text-slate-500 uppercase font-mono">Level</div>
                                                <div className="text-lg font-bold text-amber-400 font-mono">{top3[0].level}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Rank 2 & 3 Compact */}
                            <div className="grid grid-cols-2 gap-4">
                                {top3[1] && (
                                    <div className="rounded-2xl glass-panel p-4 flex flex-col items-center text-center relative overflow-hidden group hover:border-slate-500/40 transition-colors">
                                        <div className="absolute top-0 right-0 w-12 h-12 bg-slate-500/10 rounded-bl-full -mr-2 -mt-2"></div>
                                        <div className="relative mb-2">
                                            <div className="w-12 h-12 rounded-full bg-[#1a1e2e] border border-slate-600 flex items-center justify-center text-slate-400 font-bold">2</div>
                                        </div>
                                        <div className="font-bold text-slate-200 text-sm truncate w-full">{top3[1].nickname || top3[1].username}</div>
                                        <div className="text-[10px] text-slate-500 mt-1">{top3[1].xp?.toLocaleString()} XP</div>
                                    </div>
                                )}
                                {top3[2] && (
                                    <div className="rounded-2xl glass-panel p-4 flex flex-col items-center text-center relative overflow-hidden group hover:border-orange-800/40 transition-colors">
                                        <div className="absolute top-0 right-0 w-12 h-12 bg-orange-700/10 rounded-bl-full -mr-2 -mt-2"></div>
                                        <div className="relative mb-2">
                                            <div className="w-12 h-12 rounded-full bg-[#1a1e2e] border border-orange-800 flex items-center justify-center text-orange-600 font-bold">3</div>
                                        </div>
                                        <div className="font-bold text-slate-200 text-sm truncate w-full">{top3[2].nickname || top3[2].username}</div>
                                        <div className="text-[10px] text-slate-500 mt-1">{top3[2].xp?.toLocaleString()} XP</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Dense List */}
                        <div className="lg:col-span-8 glass-panel rounded-2xl overflow-hidden">
                            <div className="px-5 py-3 border-b border-white/5 bg-white/[0.02] flex justify-between items-center">
                                <span className="text-xs font-bold text-slate-400">Mission Standings</span>
                                <span className="text-[10px] text-slate-600 font-mono uppercase">Top 100 Operatives</span>
                            </div>

                            <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 hover:scrollbar-thumb-white/20">
                                {restOfRanking.length === 0 && top3.length === 0 ? (
                                    <div className="py-12 text-center text-slate-500">
                                        <p className="text-sm">No operative data found.</p>
                                    </div>
                                ) : (
                                    restOfRanking.map((user, index) => {
                                        const rank = index + 4;
                                        const isCurrentUser = myRank?.rank === rank;

                                        return (
                                            <div
                                                key={user.username}
                                                className={`flex items-center gap-4 px-5 py-3 hover:bg-white/[0.02] transition-colors group ${isCurrentUser ? 'bg-cyan-500/5' : ''}`}
                                            >
                                                <div className="w-8 text-center text-sm font-mono font-bold text-slate-600 group-hover:text-slate-400">
                                                    {rank}
                                                </div>

                                                <div className="flex-1 flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold border ${isCurrentUser
                                                        ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400'
                                                        : 'bg-[#1a1e2e] border-white/5 text-slate-500'}`}>
                                                        {user.username?.[0]?.toUpperCase() || 'U'}
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <div className={`text-sm font-bold ${isCurrentUser ? 'text-cyan-400' : 'text-slate-300'} group-hover:text-white transition-colors`}>
                                                            {user.nickname || user.username}
                                                        </div>
                                                        <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                                                            <span>{getUserFullTitle(user.level, [])}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="text-right">
                                                    <div className={`text-sm font-mono font-bold ${isCurrentUser ? 'text-cyan-400' : 'text-slate-400'} group-hover:text-white transition-colors`}>
                                                        {user.xp?.toLocaleString()} <span className="text-[10px] font-normal opacity-50">XP</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                    </div>
                )}
            </main>

            {/* 내 순위 고정 바 - Minimalist */}
            {myRank && !isLoading && (
                <div className="fixed bottom-0 left-0 right-0 z-40 glass-panel border-t border-cyan-500/20">
                    <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center text-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.2)]">
                                <i className="fas fa-crosshairs"></i>
                            </div>
                            <div>
                                <div className="text-sm font-bold text-white leading-none mb-1">
                                    {myRank.nickname || myRank.username}
                                    <span className="ml-2 text-[10px] text-cyan-500 font-mono opacity-80">YOU</span>
                                </div>
                                <div className="text-[10px] text-slate-500">
                                    {getUserFullTitle(myRank.level, [])}
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-6">
                            <div className="text-right">
                                <span className="text-[10px] text-slate-500 uppercase mr-2">Rank</span>
                                <span className="text-xl font-bold text-cyan-400 font-mono">#{myRank.rank || '-'}</span>
                            </div>
                            <div className="text-right hidden sm:block">
                                <span className="text-[10px] text-slate-500 uppercase mr-2">Total XP</span>
                                <span className="text-xl font-bold text-white font-mono">{myRank.xp?.toLocaleString() || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
